/**
 * Nesting a flat group list and a flat device list into the tree they describe.
 *
 * Kept pure and separate from rendering because the interesting failure here
 * is silent: a device whose group is missing from the list, or a group whose
 * parent is, simply stops appearing. Nothing errors, the tree just quietly
 * holds fewer devices than the organization owns. So every input is placed
 * somewhere -- an orphaned group becomes a root, and a device whose group
 * cannot be found falls into `unfiled` rather than out of the tree.
 *
 * Depth is unconstrained and devices attach at any level, including directly
 * to the organization node. "Site" and "rack" are labels the UI offers, not
 * tiers this code knows about.
 */

export interface TreeGroup {
  id: string;
  parent_id: string | null;
  name: string;
  kind: string | null;
}

export interface TreeDevice {
  id: string;
  group_id?: string | null;
  is_shared?: boolean;
}

export interface TreeNode<G extends TreeGroup, D extends TreeDevice> {
  group: G;
  children: TreeNode<G, D>[];
  /** Devices filed directly on this node, not on its descendants. */
  devices: D[];
}

export interface DeviceTree<G extends TreeGroup, D extends TreeDevice> {
  roots: TreeNode<G, D>[];
  /** Readable, but their group is not -- or they have none. Never dropped. */
  unfiled: D[];
  /** Owned by another organization, so they belong to no node in this tree. */
  shared: D[];
}

export function buildDeviceTree<G extends TreeGroup, D extends TreeDevice>(
  groups: G[],
  devices: D[],
): DeviceTree<G, D> {
  const nodes = new Map<string, TreeNode<G, D>>();
  for (const group of groups) {
    nodes.set(group.id, { group, children: [], devices: [] });
  }

  const roots: TreeNode<G, D>[] = [];
  for (const group of groups) {
    const node = nodes.get(group.id)!;
    const parent = group.parent_id ? nodes.get(group.parent_id) : undefined;
    // A group whose parent is absent is promoted to a root rather than
    // discarded: it happens whenever a grant covers a branch without covering
    // the branch above it, which is a normal use of the permission model.
    if (parent) parent.children.push(node);
    else roots.push(node);
  }

  const unfiled: D[] = [];
  const shared: D[] = [];
  for (const device of devices) {
    if (device.is_shared) {
      shared.push(device);
      continue;
    }
    const node = device.group_id ? nodes.get(device.group_id) : undefined;
    if (node) node.devices.push(device);
    else unfiled.push(device);
  }

  return { roots, unfiled, shared };
}

/** Every device at or below a node, for summarising a collapsed branch. */
export function subtreeDevices<G extends TreeGroup, D extends TreeDevice>(
  node: TreeNode<G, D>,
): D[] {
  const collected = [...node.devices];
  for (const child of node.children) collected.push(...subtreeDevices(child));
  return collected;
}

/** Depth-first walk, parents before children, for a flattened render. */
export function flattenTree<G extends TreeGroup, D extends TreeDevice>(
  nodes: TreeNode<G, D>[],
  depth = 0,
): { node: TreeNode<G, D>; depth: number }[] {
  const rows: { node: TreeNode<G, D>; depth: number }[] = [];
  for (const node of nodes) {
    rows.push({ node, depth });
    rows.push(...flattenTree(node.children, depth + 1));
  }
  return rows;
}

/**
 * Each group's full path, root first: "Valley Internet / Clearlake / Rack 2".
 *
 * An access grant is attached to one node, often deep in the tree, and a bare
 * "Rack 2" says nothing about which site it is in -- a grant list built from
 * names alone is one where two different racks read identically.
 *
 * Cycle-safe and orphan-safe, for the same reason buildDeviceTree is: a group
 * whose parent is missing or that loops back on itself must still get a path
 * rather than hanging the page or dropping out of the list.
 */
export function groupPaths<G extends TreeGroup>(groups: readonly G[]): Map<string, string> {
  const byId = new Map(groups.map((g) => [g.id, g]));
  const paths = new Map<string, string>();

  for (const group of groups) {
    const names: string[] = [];
    const seen = new Set<string>();
    let current: G | undefined = group;
    while (current && !seen.has(current.id)) {
      seen.add(current.id);
      names.unshift(current.name);
      current = current.parent_id ? byId.get(current.parent_id) : undefined;
    }
    paths.set(group.id, names.join(" / "));
  }
  return paths;
}
